import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ProcessingStage } from '@consolidocs-ui/types/file-and-folder';

export type ProcessingTooltipProps = {
  stage?: ProcessingStage | null;
  stageUpdatedAt?: string | null;
  queuedAt?: string | null;
  filesAhead?: number | null;
  typicalSeconds?: number | null;
};

// The tooltip is only mounted while open, so this ticks only while someone is
// looking. Elapsed time is computed here rather than sent by the server, because a
// server-computed value would change the folder response on every poll and defeat
// its ETag.
const TICK_MS = 15_000;

const useNow = () => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);
  return now;
};

const secondsSince = (iso: string | null | undefined, now: number) => {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  // A client clock slightly behind the server's would otherwise show a negative wait.
  return Math.max(0, (now - then) / 1000);
};

/** "less than a minute", "6 minutes", "1.5 hours", in the reader's language. */
export const formatDuration = (
  seconds: number,
  t: TFunction,
  language: string
) => {
  if (seconds < 60) return t('less than a minute');
  const [value, unit] =
    seconds < 3600
      ? [Math.round(seconds / 60), 'minute']
      : [Math.round((seconds / 3600) * 2) / 2, 'hour'];
  return new Intl.NumberFormat(language, {
    style: 'unit',
    unit,
    unitDisplay: 'long',
    maximumFractionDigits: 1,
  }).format(value);
};

// Literal t() calls rather than a lookup table, so i18n:extract can find them.
const stageLabel = (stage: string, t: TFunction): string | undefined => {
  switch (stage) {
    case 'uploading':
      return t('Uploading');
    case 'converting':
      return t('Converting to PDF');
    // ai-service takes a file within seconds of it being sent, and reports
    // nothing until it is done, so from the backend's `queued` onwards the file
    // is, for all the reader can tell, being extracted.
    case 'queued':
      return t('Extracting');
    case 'saving':
      return t('Saving results');
    default:
      return undefined;
  }
};


/**
 * What the processing spinner says on hover: the stage, how long the file has
 * been in it, and, while it is extracted, how long files usually take and how
 * many sent before it are still processing, when any are. Falls back to plain "Processing" for a file with no
 * stage — one uploaded before stages existed, or from an older backend.
 */
export const ProcessingTooltip = ({
  stage,
  stageUpdatedAt,
  queuedAt,
  filesAhead,
  typicalSeconds,
}: ProcessingTooltipProps) => {
  const { t, i18n } = useTranslation();
  const now = useNow();
  const label = stage ? stageLabel(stage, t) : undefined;

  if (!label) return <>{t('Processing')}</>;

  const isQueued = stage === 'queued';
  const elapsed = secondsSince(isQueued ? queuedAt : stageUpdatedAt, now);
  const duration = (seconds: number) =>
    formatDuration(seconds, t, i18n.language);

  const lines: string[] = [];
  if (elapsed !== null) {
    lines.push(
      isQueued
        ? t('Started {{duration}} ago', { duration: duration(elapsed) })
        : t('For {{duration}}', { duration: duration(elapsed) })
    );
  }
  // Only when something is ahead: "next in the queue" beside "Extracting" would
  // contradict it.
  if (isQueued && typeof filesAhead === 'number' && filesAhead > 0) {
    lines.push(
      t('{{count}} files sent before it are still processing', {
        count: filesAhead,
      })
    );
  }
  if (isQueued && typeof typicalSeconds === 'number') {
    lines.push(
      elapsed !== null && elapsed > typicalSeconds
        ? t('Taking longer than usual (usually about {{duration}})', {
            duration: duration(typicalSeconds),
          })
        : t('Usually takes about {{duration}}', {
            duration: duration(typicalSeconds),
          })
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography level="body-sm" sx={{ color: 'inherit', fontWeight: 'md' }}>
        {label}
      </Typography>
      {lines.map((line) => (
        <Typography
          key={line}
          level="body-xs"
          sx={{ color: 'inherit', fontVariantNumeric: 'tabular-nums' }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  );
};
